import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks";
import { useTranslation } from "react-i18next";
import PatientCard from "../../components/PatientCard";
import { useNavigate } from "react-router-dom";
import { LayoutCard } from "../../components/onboarding/layoutCard/LayoutCard";
import { GetPatientRecords, fetchSearches, getPatientRecords } from "../../hooks/getSearches";
import "./records.css";
import { useDispatch } from "react-redux";
import { Spinner } from "../../components/spinner/Spinner";
import { IconButton } from "../../components/IconButton/IconButton";
import { setPatients, setReports, setSearches } from "../../redux/immunizationsSlice";
import { errorLog, infoLog } from "../../utils/logger";
import { ImmunizationSearch } from "../../models/Interfaces";
import { createSearches } from "../../hooks/getSearchesUtil";
import { DocketAPIError, ImmunizationSearchAPI, getAPIClient } from "../../apiClient";
import { getNextPollTime } from "./SearchList";
import { API_DOMAIN, PROTOCOL } from "../../globals";
import { ErrorModal } from "../../components/modals/ErrorModal";

export function Records() {
  const allPatients = useAppSelector((store) => store.immunizations.patients);
  const [timesPolled, setTimesPolled] = useState(1);
  const searchesInQueue = useAppSelector((store) => store.immunizations.searchQueue);
  const recordsInQueue = useAppSelector((store) => store.immunizations.recordsQueue);

  const [refreshing, setRefreshing] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const [pollingTimes, setPollingTimes] = useState(1);
  const timeoutFunc = React.useRef<NodeJS.Timeout | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    //infoLog(`hash: ${hash} ${window.location.hash}`);
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  useEffect(() => {
    if (searchesOrRecordsInQueue() && pollingTimes < 11) {
      clearTimeout(timeoutFunc.current || undefined);
      timeoutFunc.current = setTimeout(updateSearches, getNextPollTime(pollingTimes));
    } else {
      clearTimeout(timeoutFunc.current || undefined);
      setRefreshing(false);
      timeoutFunc.current = null;
      setPollingTimes(1);
    }

    // Cleanup function
    return () => {
      if (timeoutFunc.current) {
        clearTimeout(timeoutFunc.current);
      }
    };
  }, [searchesInQueue, recordsInQueue, pollingTimes]);

  const updateSearches = async function () {
    try {
      await fetchSearches(dispatch);
      setPollingTimes((prevTimes) => prevTimes + 1);
    } catch (e) {
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      } else {
        errorLog(e as Error);
      }
    }
  };

  const hardRefresh = async function () {
    await getAPIClient().refreshRecords();
    setTimesPolled(timesPolled + 1);
  };

  const searchesOrRecordsInQueue = () => {
    return (
      (searchesInQueue && searchesInQueue.length > 0) ||
      (recordsInQueue && recordsInQueue.length > 0)
    );
  };

  const noPatients = () => {
    return (
      <div
        className="is-flex is-flex-direction-column is-align-items-center is-halfheight hero is-justify-content-center"
        data-testid="no-patients"
      >
        <h2 className="is-size-5 has-text-weight-bold has-text-black has-text-centered">
          {t("immunizations.search_records_message")}
        </h2>
        <br />
        <br />
        <button className="button docket-button pl-4 pr-4" onClick={() => navigate("/home/search")}>
          {t("immunizations.immunizations_add_records_button")}
        </button>
      </div>
    );
  };

  const refreshRecords = async () => {
    setRefreshing(true);
    await hardRefresh();
    await updateSearches();
  };

  const openPdf = async () => {
    window.open(`${PROTOCOL}://${API_DOMAIN}/Docket_Immunizations_FAQs_Overview.pdf`, "_blank");
  };

  const withPatients = () => {
    const totalRecords = allPatients.reduce((total, patient) => {
      if (patient.records && Array.isArray(patient.records)) {
        return total + patient.records.length;
      }
      return total;
    }, 0);

    return (
      <div className="records-container">
        <div className="is-flex is-flex-direction-row is-align-items-center is-justify-content-space-between is-flex-wrap">
          <div className="level-left">
            <p className="has-text-dark">
              You have <b>{allPatients.length}</b> record{allPatients.length === 1 ? "" : "s"} and{" "}
              <b>{totalRecords}</b> immunization{totalRecords === 1 ? "" : "s"}.
            </p>
          </div>
          <div className="level-right mt-0">
            <div className="level-item has-text-centered">
              {refreshing ? (
                <Spinner />
              ) : (
                <IconButton
                  icon="refresh"
                  onClick={() => refreshRecords()}
                  alt={"refresh results"}
                />
              )}
            </div>
            <IconButton icon="faq" onClick={() => openPdf()} alt={"FAQ"} />
          </div>
        </div>
        <div className="separater"></div>
        <ul className="patient-list">
          {allPatients.map((patient, index) => (
            <li id={index.toString()} key={index}>
              <PatientCard patient={patient} index={index} />
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="columns is-0">
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
      )}
      <div className="column is-gapless">
        {allPatients && allPatients.length > 0 ? withPatients() : noPatients()}
      </div>
    </div>
  );
}

export default Records;
